import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Star } from "lucide-react";
import { scrollToSection } from "@/components/Navbar";
import api, { inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

const LINES = ["Little dresses,", "woven for India's", "sunlit childhoods."];

export default function Hero() {
  const ref = useRef(null);
  const { setSelectedProduct } = useStore();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const blobY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const [spotlight, setSpotlight] = useState(null);

  useEffect(() => {
    api.get("/products")
      .then((r) => setSpotlight(r.data.products.find((p) => p.badge === "Dress of the Season") || null))
      .catch(() => {});
  }, []);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-center overflow-hidden pt-[72px]" data-testid="hero-section">
      <motion.div style={{ y: blobY }} className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-blush blur-3xl opacity-70" />
      <motion.div style={{ y: blobY }} className="absolute bottom-0 -left-40 w-[420px] h-[420px] rounded-full bg-mint blur-3xl opacity-70" />
      <div className="absolute inset-0 bg-dots opacity-[0.04] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-16 items-center py-20">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="font-mono text-xs tracking-[0.35em] uppercase text-sage-dark mb-8"
            data-testid="hero-eyebrow"
          >
            Made in Bangalore
          </motion.p>
          <h1 className="font-serif font-black text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight text-ink" data-testid="hero-heading">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.35 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 2 ? <em className="text-terracotta not-italic font-serif italic">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="mt-8 text-base sm:text-lg text-charcoal leading-relaxed max-w-md"
          >
            Organic cotton couture for ages 0–10 — hand-blocked, skin-safe, and tailored
            for high-mobility play. Shipped across 25,000+ Indian pincodes.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToSection("#shop")}
              className="group bg-terracotta hover:bg-terracotta-dark text-white rounded-full px-8 py-4 text-sm font-semibold transition-colors duration-300 flex items-center gap-2"
              data-testid="hero-shop-cta"
            >
              Shop the Collection
              <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform duration-300" />
            </button>
            <button
              onClick={() => scrollToSection("#size-guide")}
              className="rounded-full px-8 py-4 text-sm font-semibold border-2 border-ink/15 text-ink hover:border-terracotta hover:text-terracotta transition-colors duration-300"
              data-testid="hero-sizeguide-cta"
            >
              Find Their Size
            </button>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-8"
          >
            {[["4.9", "avg. parent rating"], ["25k+", "pincodes served"], ["100%", "organic cotton"]].map(([v, l]) => (
              <div key={l}>
                <p className="font-serif font-bold text-2xl text-ink flex items-center gap-1">
                  {v} {v === "4.9" && <Star className="w-4 h-4 fill-gold text-gold" />}
                </p>
                <p className="font-mono text-[10px] tracking-widest uppercase text-charcoal/70 mt-1">{l}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: cardY }} className="relative hidden lg:block" data-testid="hero-spotlight">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, rotate: 3 }}
            animate={{ opacity: 1, scale: 1, rotate: 2 }}
            transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-white rounded-[2rem] p-4 shadow-2xl shadow-terracotta/20 cursor-pointer"
            onClick={() => spotlight && setSelectedProduct(spotlight)}
            role="button"
            title="View Dress of the Season"
          >
            <div className="overflow-hidden rounded-3xl">
              <motion.img
                src={spotlight?.images?.[0] || "https://images.unsplash.com/photo-1578897366846-358bb1c2412a?auto=format&fit=crop&w=900&q=80"}
                alt={spotlight ? spotlight.name : "Dress of the Season spotlight"}
                className="w-full aspect-[4/5] object-cover"
                whileHover={{ scale: 1.06 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                data-testid="hero-spotlight-image"
              />
            </div>
            <div className="absolute top-8 -left-6 bg-ink text-cream rounded-full px-5 py-2.5 font-mono text-[10px] tracking-[0.25em] uppercase -rotate-6 shadow-lg">
              Dress of the Season
            </div>
            <div className="flex items-end justify-between px-3 py-4">
              <div>
                <p className="font-serif font-bold text-xl text-ink">{spotlight?.name || "Gulnaar Anarkali"}</p>
                <p className="font-mono text-[10px] tracking-widest uppercase text-charcoal/70 mt-0.5">{spotlight?.fabric || "Mulmul • Gota Trim"}</p>
              </div>
              <p className="font-serif font-bold text-2xl text-terracotta">{inr(spotlight?.price || 2499)}</p>
            </div>
          </motion.div>
          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -bottom-8 -left-10 bg-mint rounded-2xl px-5 py-4 shadow-xl -rotate-3"
          >
            <p className="font-mono text-[10px] tracking-widest uppercase text-sage-dark">Free Shipping</p>
            <p className="font-serif font-bold text-ink">On orders above ₹1,999</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
